import { AnalyticsMetricId, AnalyticsTableRow } from 'services/user'
import { DimensionId, GridRow } from '../types'
import { displayName } from './display-names'

// Adapters between the segmentation table (which speaks GridRow + local column
// keys) and the aggregation engine (which speaks metric ids + live cells).

/** Bad clicks as a share of the row's clicks. Derived rather than fetched so the
 *  grid's rendered rate always reconciles with the counts printed beside it. */
export const badTrafficRate = (row: GridRow) =>
	row.clicks ? (row.veracity.invalid / row.clicks) * 100 : 0

// Table metric-column key → backend metric id, for server-side sorting.
export const COL_KEY_TO_METRIC_ID: Record<string, AnalyticsMetricId> = {
	analyzed: 'analyzed_ad_clicks',
	valid: 'valid_ad_clicks',
	suspicious: 'suspicious_ad_clicks',
	bad: 'bad_ad_clicks',
	rate: 'bad_traffic_rate',
	blocked: 'blocked_sources',
	waste: 'prevented_ad_waste'
}

// Local rate-filter value → backend filter.
export const RATE_FILTER_TO_API: Record<
	string,
	'all' | 'bad_rate_gt_10' | 'bad_rate_gt_25'
> = {
	all: 'all',
	gt10: 'bad_rate_gt_10',
	gt25: 'bad_rate_gt_25'
}

const cell = (row: AnalyticsTableRow, id: AnalyticsMetricId): number =>
	row.metrics[id]?.value ?? 0

// Map a live table row to the grid's GridRow shape. `idPrefix` makes child-row
// ids unique per drill path (a value can recur under different parents).
export const liveRowToGridRow = (
	row: AnalyticsTableRow,
	dimensionId: DimensionId,
	idPrefix = ''
): GridRow => ({
	id: idPrefix ? `${idPrefix}>${row.key}` : row.key || '(none)',
	// The same display name the charts print for this value (DEV-2432 §2.3);
	// `valueId` keeps the raw key for drill-down and icons.
	label: displayName(dimensionId, row.key, row.label),
	dimensionId,
	valueId: row.key,
	clicks: cell(row, 'analyzed_ad_clicks'),
	veracity: {
		valid: cell(row, 'valid_ad_clicks'),
		suspicious: cell(row, 'suspicious_ad_clicks'),
		invalid: cell(row, 'bad_ad_clicks')
	},
	blockedSources: cell(row, 'blocked_sources'),
	waste: row.metrics.prevented_ad_waste?.value ?? null,
	hasChildren: row.hasChildren,
	// Leading-icon hint (ad-platform code / country ISO) for source-icon.
	iconMeta: row.meta ?? null,
	// Timestamped is part of the shared type but unused by the grid renderer.
	created_at: ''
})
